import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, ShoppingBag, User, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const NAV_LINKS = [
  { to: "/shop", label: "Shop" },
  { to: "/subscribe", label: "Subscribe" },
  { to: "/about", label: "Story" },
  { to: "/markets", label: "Markets" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const { count, setOpen: setCartOpen } = useCart();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        data-testid="site-nav"
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-40 transition-[background,border,padding] duration-500 ${
          scrolled
            ? "bg-[color:var(--sr-bg)]/90 backdrop-blur-xl border-b hairline-light"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className={`sr-container flex items-center justify-between transition-[height] duration-500 ${scrolled ? "h-20 md:h-24" : "h-24 md:h-32"}`}>
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center group"
            onClick={() => setMenuOpen(false)}
          >
            <img
              src="/logo.png"
              alt="The Smiling Roots"
              className={`transition-[height] duration-500 ${
                scrolled ? "h-16 md:h-20" : "h-20 md:h-28"
              } w-auto`}
              draggable="false"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `label-eyebrow relative hover:text-[color:var(--sr-primary)] transition-colors ${
                    isActive
                      ? "text-[color:var(--sr-primary)]"
                      : "text-[color:var(--sr-text)]/75"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-4">
            <Link
              to={user ? "/account" : "/login"}
              data-testid={user ? "nav-account-link" : "nav-login-link"}
              className="hidden sm:flex items-center gap-2 px-3 py-2 label-eyebrow text-[color:var(--sr-text)] hover:text-[color:var(--sr-primary)]"
              aria-label={user ? "Your account" : "Sign in"}
            >
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  className="w-5 h-5 rounded-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <User size={18} strokeWidth={1.25} />
              )}
              <span>{user ? "Account" : "Sign in"}</span>
            </Link>
            <button
              type="button"
              data-testid="open-cart-btn"
              onClick={() => setCartOpen(true)}
              className="group relative flex items-center gap-2 px-3 py-2 min-h-[44px] label-eyebrow text-[color:var(--sr-text)] hover:text-[color:var(--sr-primary)]"
              aria-label="Open cart"
            >
              <ShoppingBag size={18} strokeWidth={1.25} />
              <span className="hidden sm:inline">Bag</span>
              <span
                data-testid="cart-count"
                className="ml-1 min-w-5 h-5 px-1 inline-flex items-center justify-center text-[10px] font-medium tracking-normal rounded-full bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)]"
              >
                {count}
              </span>
            </button>
            <button
              type="button"
              data-testid="mobile-menu-btn"
              onClick={() => setMenuOpen((v) => !v)}
              className="md:hidden p-3 -mr-3 min-h-[44px] min-w-[44px] flex items-center justify-center text-[color:var(--sr-text)]"
              aria-label="Toggle menu"
            >
              {menuOpen ? (
                <X size={22} strokeWidth={1.25} />
              ) : (
                <Menu size={22} strokeWidth={1.25} />
              )}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-30 md:hidden bg-[color:var(--sr-bg)] pt-24 px-6"
          >
            <ul className="flex flex-col gap-6">
              {NAV_LINKS.map((l, idx) => (
                <motion.li
                  key={l.to}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.08 * idx }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    onClick={() => setMenuOpen(false)}
                    className="font-serif text-5xl leading-none text-[color:var(--sr-primary)] tracking-tight"
                  >
                    {l.label}
                  </Link>
                </motion.li>
              ))}
              <motion.li
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.08 * NAV_LINKS.length }}
              >
                <Link
                  to={user ? "/account" : "/login"}
                  data-testid={user ? "mobile-nav-account-link" : "mobile-nav-login-link"}
                  onClick={() => setMenuOpen(false)}
                  className="font-serif text-5xl leading-none text-[color:var(--sr-primary)] tracking-tight italic font-light"
                >
                  {user ? "Account" : "Sign in"}
                </Link>
              </motion.li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
